import React, { memo, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import BotAvatarIcon from './icons/BotAvatarIcon';
import Draggable from 'react-draggable';

const POSITION_STORAGE_KEY = 'castle-chatbot-launcher-position';

const getStoredPosition = () => {
  try {
    const raw = window.localStorage.getItem(POSITION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : { x: 0, y: 0 };
  } catch (error) {
    return { x: 0, y: 0 };
  }
};

const AIAssistantLauncher = ({ isOpen, onToggle, unreadCount }) => {
  const tooltipId = useId();
  const badgeText = unreadCount > 0 ? (unreadCount > 99 ? '99+' : unreadCount) : 'NEW';
  const nodeRef = useRef(null);
  const wasDragged = useRef(false);
  const [position, setPosition] = useState(getStoredPosition);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const handleStart = (event, data) => {
    dragStartRef.current = { x: data.x, y: data.y };
  };

  const handleStop = (event, data) => {
    const movedX = Math.abs(data.x - dragStartRef.current.x);
    const movedY = Math.abs(data.y - dragStartRef.current.y);
    wasDragged.current = movedX > 3 || movedY > 3;

    setPosition({ x: data.x, y: data.y });
    window.localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify({ x: data.x, y: data.y }));
  };

  const handleClick = (event) => {
    if (wasDragged.current) {
      event.preventDefault();
      wasDragged.current = false;
      return;
    }
    onToggle();
  };

  return (
    <Draggable nodeRef={nodeRef} position={position} bounds="body" onStart={handleStart} onStop={handleStop}>
      <div ref={nodeRef} className="launcher-button-container">
        <div className="launcher-tooltip" id={tooltipId} role="tooltip">
          Need help? Chat with Cassie
        </div>
        <button
          type="button"
          className="launcher-button"
          onClick={handleClick}
          aria-label="Open AI Chat Assistant"
          aria-describedby={tooltipId}
          aria-expanded={isOpen}
        >
          <BotAvatarIcon className="launcher-button-icon" />
          <span className="launcher-button-badge" aria-live="polite">
            {badgeText}
          </span>
        </button>
        <div className="launcher-label">Cassie</div>
      </div>
    </Draggable>
  );
};

AIAssistantLauncher.propTypes = {
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  unreadCount: PropTypes.number
};

AIAssistantLauncher.defaultProps = {
  isOpen: false,
  unreadCount: 0
};

export default memo(AIAssistantLauncher);

